/**
 * RecordFormDialog - Formulário de registro de atividades diárias
 * 
 * Extraído do App.js durante refatoração
 * 
 * Funcionalidades:
 * - Registro de sangria e produção
 * - Controle de reposições e sangria perdida
 * - Aplicação de químicos (fungicida, ethrel)
 * - Registro de chuva e atividades extras
 * - Controle de equipe e avaliação interna
 * - Sistema de avaliação privado para empregadores
 * 
 * Dependências:
 * - useTranslation (traduções)
 * - axios (requisições API)
 * - Dialog, Input, Label, Textarea, Button (shadcn/ui)
 */

import React, { useState, useEffect } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";
import { Checkbox } from "./ui/checkbox";
import { useTranslation } from "../translations";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Info icon component - ícone de informação intuitivo
const HelpIcon = ({ tooltip, color = "gray" }) => (
  <TooltipProvider>
    <Tooltip>
      <TooltipTrigger asChild>
        <button type="button" className={`ml-1 inline-flex items-center justify-center w-4 h-4 text-${color}-500 hover:text-${color}-700 transition-colors cursor-help`}>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a.75.75 0 000 1.5h.253a.25.25 0 01.244.304l-.459 2.066A1.75 1.75 0 0010.747 15H11a.75.75 0 000-1.5h-.253a.25.25 0 01-.244-.304l.459-2.066A1.75 1.75 0 009.253 9H9z" clipRule="evenodd" />
          </svg>
        </button>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs bg-white text-gray-800 p-3 rounded-lg shadow-xl border border-gray-200 z-50">
        <p className="text-sm leading-relaxed">{tooltip}</p>
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

const RecordFormDialog = ({ open, onClose, property, date, existingRecord, existingDayRecords = [], onRefresh, onComplete }) => {
  const { t } = useTranslation();
  
  // Verificar se o dia já tem registros (para pré-preenchimento ao adicionar novo trabalhador)
  const isAddingToExistingDay = !existingRecord && existingDayRecords.length > 0;
  const dayReferenceData = existingDayRecords.length > 0 ? existingDayRecords[0] : null;
  const [formData, setFormData] = useState({
    sangria: false,
    talhao_lote_sangria: '',
    sangrias: [],
    reposicao: '',
    talhao_lote_reposicao: '',
    reposicoes: [],
    sangria_perdida: '',
    talhao_lote_sangria_perdida: '',
    fungicida: false,
    talhao_lote_fungicida: '',
    fungicida_kg: '',
    ethrel: false,
    talhao_lote_ethrel: '',
    ethrel_proporcao: '',
    ethrel_litros: '',
    protetor_qtd: '',
    herbicida_litros: '',
    atividade_extra: '',
    chuva_mm: '',
    producao_kg: '',
    observacoes: '',
    // Campos opcionais para avaliação interna (privado)
    trabalhadores: '',
    avaliacao_interna: '',
    use_internal_evaluation: false,
    // Novos campos para controle de equipe (EXPANSÃO MINIMALISTA)
    trabalhador_responsavel: '',
    plantas_talhao: '',
    // Campos de coleta para cálculo correto de kg/planta/corte
    // Nota: data_coleta é preenchida automaticamente com a data do registro
    coleta_realizada: false,
    lotes_coletados: [],
    // Compensação de falta (opcional)
    compensa_falta_id: ''
  });
  const [loading, setLoading] = useState(false);
  // Faltas pendentes do trabalhador selecionado (para compensação)
  const [pendingAbsences, setPendingAbsences] = useState([]);
  
  // Configuração de lotes baseado no sistema de sangria da propriedade
  const getSistemaSangriaLotes = () => {
    const sistema = property?.sistema_sangria || 'D/4';
    const numLotes = parseInt(sistema.replace('D/', '')) || 4;
    return Array.from({ length: numLotes }, (_, i) => `Lot ${i + 1}`);
  };
  const lotesConfig = property?.lotes_config || getSistemaSangriaLotes();

  useEffect(() => {
    if (existingRecord) {
      setFormData({
        sangria: existingRecord.sangria || false,
        talhao_lote_sangria: existingRecord.talhao_lote_sangria || '',
        sangrias: existingRecord.sangrias?.length > 0
          ? existingRecord.sangrias.map(s => ({lote: s.lote || '', valor: s.valor?.toString() || ''}))
          : (existingRecord.sangria && existingRecord.talhao_lote_sangria
            ? [{lote: existingRecord.talhao_lote_sangria, valor: '1'}]
            : []),
        reposicao: existingRecord.reposicao !== undefined ? existingRecord.reposicao.toString() : '',
        talhao_lote_reposicao: existingRecord.talhao_lote_reposicao || '',
        reposicoes: existingRecord.reposicoes?.length > 0
          ? existingRecord.reposicoes.map(r => ({lote: r.lote || '', valor: r.valor?.toString() || ''}))
          : (existingRecord.reposicao > 0
            ? [{lote: existingRecord.talhao_lote_reposicao || '', valor: existingRecord.reposicao.toString()}]
            : []),
        sangria_perdida: existingRecord.sangria_perdida !== undefined ? existingRecord.sangria_perdida.toString() : '',
        talhao_lote_sangria_perdida: existingRecord.talhao_lote_sangria_perdida || '',
        fungicida: existingRecord.fungicida || false,
        talhao_lote_fungicida: existingRecord.talhao_lote_fungicida || '',
        ethrel: existingRecord.ethrel || false,
        talhao_lote_ethrel: existingRecord.talhao_lote_ethrel || '',
        ethrel_proporcao: existingRecord.ethrel_proporcao || '',
        atividade_extra: existingRecord.atividade_extra || '',
        chuva_mm: existingRecord.chuva_mm !== undefined ? existingRecord.chuva_mm.toString() : '',
        producao_kg: existingRecord.producao_kg !== undefined ? existingRecord.producao_kg.toString() : '',
        observacoes: existingRecord.observacoes || '',
        trabalhador_responsavel: existingRecord.trabalhador_responsavel || '',
        plantas_talhao: existingRecord.plantas_talhao !== undefined ? existingRecord.plantas_talhao.toString() : '',
        coleta_realizada: existingRecord.coleta_realizada || false,
        lotes_coletados: existingRecord.lotes_coletados || [],
        ethrel_litros: existingRecord.ethrel_litros ? existingRecord.ethrel_litros.toString() : '',
        fungicida_kg: existingRecord.fungicida_kg ? existingRecord.fungicida_kg.toString() : '',
        protetor_qtd: existingRecord.protetor_qtd ? existingRecord.protetor_qtd.toString() : '',
        herbicida_litros: existingRecord.herbicida_litros ? existingRecord.herbicida_litros.toString() : ''
      });
    } else if (isAddingToExistingDay && dayReferenceData) {
      setFormData({
        sangria: false,
        talhao_lote_sangria: '',
        sangrias: [],
        reposicao: dayReferenceData.reposicao !== undefined ? dayReferenceData.reposicao.toString() : '',
        talhao_lote_reposicao: dayReferenceData.talhao_lote_reposicao || '',
        reposicoes: dayReferenceData.reposicoes?.length > 0
          ? dayReferenceData.reposicoes.map(r => ({lote: r.lote || '', valor: r.valor?.toString() || ''}))
          : [],
        sangria_perdida: dayReferenceData.sangria_perdida !== undefined ? dayReferenceData.sangria_perdida.toString() : '',
        talhao_lote_sangria_perdida: dayReferenceData.talhao_lote_sangria_perdida || '',
        fungicida: dayReferenceData.fungicida || false,
        talhao_lote_fungicida: dayReferenceData.talhao_lote_fungicida || '',
        ethrel: dayReferenceData.ethrel || false,
        talhao_lote_ethrel: dayReferenceData.talhao_lote_ethrel || '',
        ethrel_proporcao: dayReferenceData.ethrel_proporcao || '',
        atividade_extra: '',
        chuva_mm: dayReferenceData.chuva_mm !== undefined ? dayReferenceData.chuva_mm.toString() : '',
        producao_kg: '',
        observacoes: '',
        trabalhador_responsavel: '',
        plantas_talhao: '',
        coleta_realizada: dayReferenceData.coleta_realizada || false,
        lotes_coletados: dayReferenceData.lotes_coletados || [],
        avaliacao_interna: '',
        use_internal_evaluation: false
      });
    } else {
      setFormData({
        sangria: false,
        talhao_lote_sangria: '',
        sangrias: [],
        reposicao: '',
        talhao_lote_reposicao: '',
        reposicoes: [],
        sangria_perdida: '',
        talhao_lote_sangria_perdida: '',
        fungicida: false,
        talhao_lote_fungicida: '',
        ethrel: false,
        talhao_lote_ethrel: '',
        ethrel_proporcao: '',
        atividade_extra: '',
        chuva_mm: '',
        producao_kg: '',
        observacoes: '',
        // Novos campos limpos
        trabalhador_responsavel: '',
        plantas_talhao: '',
        // Campos de coleta
        coleta_realizada: false,
        lotes_coletados: []
      });
    }
  }, [existingRecord, isAddingToExistingDay, open]);

  // Buscar faltas pendentes do trabalhador (para opção de compensação)
  useEffect(() => {
    const fetchPending = async () => {
      const worker = formData.trabalhador_responsavel?.trim();
      const sangriou = formData.sangria || (formData.sangrias && formData.sangrias.length > 0);
      if (!property?.id || !worker || !sangriou) {
        setPendingAbsences([]);
        return;
      }
      try {
        const res = await axios.get(`${API}/calendar-events`, {
          params: {
            property_id: property.id,
            trabalhador_responsavel: worker,
            pending_only: true
          }
        });
        // Mostrar apenas faltas anteriores à data atual do registro
        const filtered = (res.data || []).filter(f => f.date < date);
        setPendingAbsences(filtered);
      } catch (err) {
        console.error('Erro ao buscar faltas pendentes:', err);
        setPendingAbsences([]);
      }
    };
    fetchPending();
  }, [formData.trabalhador_responsavel, formData.sangria, formData.sangrias, property?.id, date]);

  // Estado para controlar se mostra opção de duplicar após salvar
  const [showDuplicateOption, setShowDuplicateOption] = useState(false);
  const [lastSavedData, setLastSavedData] = useState(null);

  const executarBaixaEstoque = async () => {
    const itens = [];
    if (formData.ethrel && parseFloat(formData.ethrel_litros) > 0) itens.push({ produto: 'Ethrel (estimulante)', quantidade: parseFloat(formData.ethrel_litros), obs: `Ethrel ${formData.talhao_lote_ethrel || ''}`.trim() });
    if (formData.fungicida && parseFloat(formData.fungicida_kg) > 0) itens.push({ produto: 'Pasta fungicida', quantidade: parseFloat(formData.fungicida_kg), obs: `Fungicida ${formData.talhao_lote_fungicida || ''}`.trim() });
    if (parseFloat(formData.protetor_qtd) > 0) itens.push({ produto: 'Protetor de chuva', quantidade: parseFloat(formData.protetor_qtd), obs: 'Protetores colocados/repostos' });
    if (parseFloat(formData.herbicida_litros) > 0) itens.push({ produto: 'Herbicida', quantidade: parseFloat(formData.herbicida_litros), obs: 'Aplicação de herbicida' });
    if (!itens.length) return '';
    try {
      const r = await axios.post(`${API}/financeiro/estoque/baixa-rapida`, { itens, date });
      if (r.data.ignorado) return '';
      const ok = (r.data.baixas || []).filter((b) => b.ok);
      const falhas = (r.data.baixas || []).filter((b) => !b.ok);
      let msg = '';
      if (ok.length) msg += `\n\n📦 Estoque baixado: ${ok.map((b) => `${b.produto} −${b.baixado}${b.unidade ? ' ' + b.unidade : ''}${b.alerta ? ' ⚠️ no mínimo!' : ''}`).join(', ')}`;
      if (falhas.length) msg += `\n⚠️ Sem baixa: ${falhas.map((b) => `${b.produto} (${b.motivo})`).join(', ')}`;
      return msg;
    } catch { return ''; }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      // Computar sangrias e campos backward-compatible
      const sangriasLimpas = (formData.sangrias || [])
        .filter(s => s.lote && parseFloat(s.valor) > 0)
        .map(s => ({ lote: (s.lote || '').trim(), valor: parseFloat(s.valor) || 0 }));
      const sangriaLotes = sangriasLimpas.filter(s => s.lote).map(s => s.lote).join(', ');
      
      // Computar reposicao e talhao_lote_reposicao a partir do array reposicoes
      const reposicoesLimpas = (formData.reposicoes || [])
        .filter(r => parseFloat(r.valor) > 0)
        .map(r => ({ lote: (r.lote || '').trim(), valor: parseFloat(r.valor) || 0 }));
      const repoTotal = reposicoesLimpas.reduce((sum, r) => sum + r.valor, 0);
      const repoLotes = reposicoesLimpas.filter(r => r.lote).map(r => r.lote).join(', ');
      
      const submitData = {
        ...formData,
        sangrias: sangriasLimpas,
        sangria: sangriasLimpas.length > 0,
        talhao_lote_sangria: sangriaLotes,
        reposicao: repoTotal,
        talhao_lote_reposicao: repoLotes,
        reposicoes: reposicoesLimpas,
        sangria_perdida: parseInt(formData.sangria_perdida) || 0,
        chuva_mm: parseFloat(formData.chuva_mm) || 0,
        producao_kg: parseFloat(formData.producao_kg) || 0,
        plantas_talhao: parseInt(formData.plantas_talhao) || 0,
        ethrel_litros: parseFloat(formData.ethrel_litros) || 0,
        fungicida_kg: parseFloat(formData.fungicida_kg) || 0,
        protetor_qtd: parseFloat(formData.protetor_qtd) || 0,
        herbicida_litros: parseFloat(formData.herbicida_litros) || 0,
        // Campos de coleta - data_coleta usa a data do registro
        coleta_realizada: formData.coleta_realizada || false,
        data_coleta: formData.coleta_realizada ? date : null,
        lotes_coletados: formData.lotes_coletados || [],
        // Compensação de falta (opcional)
        compensa_falta_id: formData.compensa_falta_id || null
      };
      
      if (existingRecord) {
        // Update existing record
        await axios.put(`${API}/bleeding-records/${existingRecord.id}`, submitData);
        alert("Registro atualizado com sucesso!");
        window.dispatchEvent(new Event('heveatalent-progress-update'));
        onComplete();
      } else {
        // Create new record
        submitData.property_id = property.id;
        submitData.date = date;
        await axios.post(`${API}/bleeding-records`, submitData);
        const baixaMsg = await executarBaixaEstoque();
        
        // Atualizar dados na tabela IMEDIATAMENTE (sem fechar o form)
        if (onRefresh) onRefresh();
        
        // Se tem trabalhador, mostra opção de duplicar
        if (formData.trabalhador_responsavel) {
          if (baixaMsg) alert(baixaMsg.trim());
          setLastSavedData({...formData});
          setShowDuplicateOption(true);
          // Não fecha o modal, aguarda decisão do usuário
        } else {
          // Sem trabalhador, fecha normalmente
          alert("Registro criado com sucesso!" + baixaMsg);
          window.dispatchEvent(new Event('heveatalent-progress-update'));
          onComplete();
        }
      }
    } catch (error) {
      alert("Erro ao salvar registro: " + (error.response?.data?.detail || error.message));
    }
    
    setLoading(false);
  };

  // Função para duplicar registro para outro trabalhador
  const handleDuplicate = () => {
    if (lastSavedData) {
      // Manter dados do dia pré-preenchidos, limpar dados individuais do trabalhador
      // IMPORTANTE: sangrias NÃO são copiadas - cada trabalhador deve informar seus próprios cortes
      setFormData({
        sangria: false,
        talhao_lote_sangria: '',
        sangrias: [],
        reposicao: lastSavedData.reposicao || '',
        talhao_lote_reposicao: lastSavedData.talhao_lote_reposicao || '',
        reposicoes: lastSavedData.reposicoes || [],
        sangria_perdida: lastSavedData.sangria_perdida || '',
        talhao_lote_sangria_perdida: lastSavedData.talhao_lote_sangria_perdida || '',
        fungicida: lastSavedData.fungicida || false,
        talhao_lote_fungicida: lastSavedData.talhao_lote_fungicida || '',
        ethrel: lastSavedData.ethrel || false,
        talhao_lote_ethrel: lastSavedData.talhao_lote_ethrel || '',
        ethrel_proporcao: lastSavedData.ethrel_proporcao || '',
        atividade_extra: '',
        chuva_mm: lastSavedData.chuva_mm || '',
        producao_kg: '',
        observacoes: '',
        trabalhador_responsavel: '',
        plantas_talhao: '',
        coleta_realizada: lastSavedData.coleta_realizada || false,
        lotes_coletados: lastSavedData.lotes_coletados || [],
        avaliacao_interna: '',
        use_internal_evaluation: false,
        trabalhadores: ''
      });
      setShowDuplicateOption(false);
    }
  };

  // Função para fechar sem duplicar
  const handleCloseWithoutDuplicate = () => {
    setShowDuplicateOption(false);
    setLastSavedData(null);
    onComplete();
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            📝 {existingRecord ? t('edit') : t('new')} {t('record')} - {t('day')} {new Date(date + 'T00:00:00').getDate()}
          </DialogTitle>
          <DialogDescription>
            {property.name} - {new Date(date + 'T00:00:00').toLocaleDateString('pt-BR')}
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Banner de pré-preenchimento quando adicionando trabalhador a dia existente */}
          {isAddingToExistingDay && dayReferenceData && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3" data-testid="prefill-banner">
              <p className="text-sm text-blue-700 font-medium">
                Valores pré-preenchidos com base nos dados do dia. Altere se necessário.
              </p>
              {existingDayRecords.filter(r => r.trabalhador_responsavel).length > 0 && (
                <p className="text-xs text-blue-500 mt-1">
                  Trabalhadores já registrados: {existingDayRecords.filter(r => r.trabalhador_responsavel).map(r => r.trabalhador_responsavel).join(', ')}
                </p>
              )}
            </div>
          )}

          {/* FORMULÁRIO ÚNICO - Todos os campos sempre visíveis */}
          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={formData.sangria || (formData.sangrias && formData.sangrias.length > 0)}
                onChange={(e) => {
                  if (e.target.checked) {
                    setFormData({...formData, sangria: true, sangrias: formData.sangrias.length > 0 ? formData.sangrias : [{lote: '', valor: '1'}]});
                  } else {
                    setFormData({...formData, sangria: false, sangrias: []});
                  }
                }}
                className="w-4 h-4"
                id="sangria-checkbox"
                data-testid="sangria-checkbox"
              />
              <Label htmlFor="sangria-checkbox" className="text-base font-medium flex items-center gap-1">
                <img src="/icons/faca_sangria.png" alt="" className="w-5 h-5" /> Sangria Realizada
                <HelpIcon tooltip={t('tooltipSangria')} />
              </Label>
            </div>
            
            {(formData.sangria || (formData.sangrias && formData.sangrias.length > 0)) && (
              <div className="pl-6 border-l-2 border-green-200 bg-green-50 p-3 rounded space-y-2" data-testid="sangrias-list">
                <Label className="text-sm font-medium flex items-center gap-1">📍 Lotes Sangrados</Label>
                {(formData.sangrias || []).map((sangria, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <select
                      value={sangria.lote}
                      onChange={(e) => {
                        const updated = [...formData.sangrias];
                        updated[index] = {...updated[index], lote: e.target.value};
                        setFormData({...formData, sangrias: updated});
                      }}
                      className="flex-1 h-9 rounded-md border border-input bg-white px-3 text-sm"
                      data-testid={`sangria-lote-${index}`}
                    >
                      <option value="">{t('rfSelectLot')}</option>
                      {lotesConfig.map(lote => (
                        <option key={lote} value={lote}>{lote}</option>
                      ))}
                    </select>
                    <Input
                      type="number"
                      step="0.5"
                      min="0.5"
                      max="1"
                      value={sangria.valor}
                      onChange={(e) => {
                        const updated = [...formData.sangrias];
                        updated[index] = {...updated[index], valor: e.target.value};
                        setFormData({...formData, sangrias: updated});
                      }}
                      className="w-20"
                      placeholder="1"
                      data-testid={`sangria-valor-${index}`}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        const updated = formData.sangrias.filter((_, i) => i !== index);
                        setFormData({...formData, sangrias: updated, sangria: updated.length > 0});
                      }}
                      className="text-red-500 hover:text-red-700 px-2"
                      data-testid={`sangria-remove-${index}`}
                    >
                      ✕
                    </Button>
                  </div>
                ))}
                <div className="flex items-center justify-between">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setFormData({...formData, sangrias: [...(formData.sangrias || []), {lote: '', valor: '1'}]})}
                    className="text-green-600 border-green-300"
                    data-testid="add-sangria-btn"
                  >
                    + Adicionar lote sangrado
                  </Button>
                  <span className="text-xs text-gray-500">
                    Total: {(formData.sangrias || []).reduce((sum, s) => sum + (parseFloat(s.valor) || 0), 0)} corte(s)
                  </span>
                </div>
                <p className="text-xs text-gray-500">0.5 = meio lote, 1 = lote inteiro. Meios lotes somam para formar cortes completos.</p>
              </div>
            )}
          </div>

          {/* Seção de Coleta - INDEPENDENTE da sangria (pode coletar mesmo sem sangrar no dia) */}
          <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-center gap-2 mb-3">
              <Checkbox
                id="coleta_realizada"
                checked={formData.coleta_realizada}
                onCheckedChange={(checked) => {
                  // Quando marca coleta, pré-seleciona todos os lotes
                  if (checked) {
                    setFormData({
                      ...formData, 
                      coleta_realizada: true,
                      lotes_coletados: [...lotesConfig] // Todos os lotes pré-selecionados
                    });
                  } else {
                    setFormData({...formData, coleta_realizada: false, lotes_coletados: []});
                  }
                }}
              />
              <Label htmlFor="coleta_realizada" className="font-medium text-blue-800 cursor-pointer">
                📦 Registrar Coleta de Borracha
              </Label>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="text-blue-500 cursor-help">ⓘ</span>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs bg-white p-2 shadow-lg border">
                    <p className="text-xs">Marque quando recolher a borracha dos lotes. Pode ser feito mesmo sem sangria no dia (ex: chuva). A pesagem pode ser informada depois.</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            
            {formData.coleta_realizada && (
              <div className="space-y-3">
                <div>
                  <Label className="text-sm text-blue-700 mb-2 block">☑️ Lotes Coletados</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {lotesConfig.map((lote) => (
                      <div key={lote} className="flex items-center gap-2 p-2 bg-white rounded border">
                        <Checkbox
                          id={`lote-${lote}`}
                          checked={formData.lotes_coletados.includes(lote)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setFormData({
                                ...formData,
                                lotes_coletados: [...formData.lotes_coletados, lote]
                              });
                            } else {
                              setFormData({
                                ...formData,
                                lotes_coletados: formData.lotes_coletados.filter(l => l !== lote)
                              });
                            }
                          }}
                        />
                        <Label htmlFor={`lote-${lote}`} className="text-sm cursor-pointer">{lote}</Label>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    ✅ {t('rfAllLotsMarked')} <strong>{t('rfUncheckNotCollected')}</strong> {t('rfNotCollectedText')}
                  </p>
                  {formData.lotes_coletados.length < lotesConfig.length && (
                    <div className="mt-2 p-2 bg-orange-50 border border-orange-200 rounded">
                      <p className="text-xs text-orange-700 font-medium">
                        ⚠️ {lotesConfig.length - formData.lotes_coletados.length} lote(s) não coletado(s)
                      </p>
                      <p className="text-xs text-orange-600 mt-1">
                        Os cortes deste(s) lote(s) continuarão acumulando até a próxima coleta.
                      </p>
                    </div>
                  )}
                </div>
                
                {/* Campo de produção dentro da coleta para facilitar */}
                <div className="pt-3 border-t border-blue-200">
                  <Label className="text-sm text-blue-700 flex items-center gap-1">
                    ⚖️ Produção (kg) 
                    <span className="text-xs text-gray-500 font-normal">- opcional, pode informar depois</span>
                  </Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={formData.producao_kg}
                    onChange={(e) => setFormData({...formData, producao_kg: e.target.value})}
                    placeholder="Deixe em branco se ainda não pesou"
                    min="0"
                    className="mt-1"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Reposições - largura total para acomodar lista dinâmica */}
          <div className="space-y-2">
            <Label className="flex items-center">🔄 Reposições<HelpIcon tooltip={t('tooltipReposicao')} /></Label>
            {formData.reposicoes.length > 0 && (
              <div className="space-y-2">
                {formData.reposicoes.map((repo, idx) => (
                  <div key={idx} className="flex gap-2 items-center">
                    <Input
                      type="number"
                      step="0.5"
                      value={repo.valor}
                      onChange={(e) => {
                        const updated = [...formData.reposicoes];
                        updated[idx] = {...updated[idx], valor: e.target.value};
                        setFormData({...formData, reposicoes: updated});
                      }}
                      placeholder="0.5"
                      min="0"
                      className="w-24"
                    />
                    <select
                      value={repo.lote}
                      onChange={(e) => {
                        const updated = [...formData.reposicoes];
                        updated[idx] = {...updated[idx], lote: e.target.value};
                        setFormData({...formData, reposicoes: updated});
                      }}
                      className="flex-1 h-10 rounded-md border border-input bg-background px-3 text-sm"
                    >
                      <option value="">{t('rfSelectLotOption')}</option>
                      {lotesConfig.map(lote => (
                        <option key={lote} value={lote}>{lote}</option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => {
                        const updated = formData.reposicoes.filter((_, i) => i !== idx);
                        setFormData({...formData, reposicoes: updated});
                      }}
                      className="text-red-400 hover:text-red-600 text-sm px-2 shrink-0"
                    >
                      ✕
                    </button>
                  </div>
                ))}
                <p className="text-xs text-gray-500">
                  Total: {formData.reposicoes.reduce((s, r) => s + (parseFloat(r.valor) || 0), 0)} reposição(ões)
                </p>
              </div>
            )}
            <button
              type="button"
              onClick={() => setFormData({...formData, reposicoes: [...formData.reposicoes, {lote: '', valor: ''}]})}
              className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
            >
              + Adicionar reposição
            </button>
            <p className="text-xs text-gray-400">0.5 = meio lote. Pode adicionar múltiplos lotes.</p>
          </div>

          {/* Sangria Perdida */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="flex items-center">❌ Sangria Perdida<HelpIcon tooltip={t('tooltipSangriaPerdida')} /></Label>
              <Input
                type="number"
                value={formData.sangria_perdida}
                onChange={(e) => setFormData({...formData, sangria_perdida: e.target.value})}
                placeholder="0"
                min="0"
              />
              {formData.sangria_perdida && parseInt(formData.sangria_perdida) > 0 && (
                <div>
                  <Label className="text-sm">📍 Talhão/Lote das Perdas</Label>
                  <Input
                    type="text"
                    value={formData.talhao_lote_sangria_perdida}
                    onChange={(e) => setFormData({...formData, talhao_lote_sangria_perdida: e.target.value})}
                    placeholder="Ex: Talhão C1..."
                    className="text-sm"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Chemicals */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.fungicida}
                  onChange={(e) => setFormData({...formData, fungicida: e.target.checked})}
                  className="mr-2"
                />
                🧪 Fungicida
                <HelpIcon tooltip={t('tooltipFungicida')} />
              </Label>
              {formData.fungicida && (
                <div className="space-y-2">
                  <div>
                    <Label className="text-sm">📍 Talhão/Lote do Fungicida</Label>
                    <Input
                      type="text"
                      value={formData.talhao_lote_fungicida}
                      onChange={(e) => setFormData({...formData, talhao_lote_fungicida: e.target.value})}
                      placeholder="Ex: Talhão D3..."
                      className="text-sm"
                    />
                  </div>
                  <div>
                    <Label className="text-sm">📦 Qtd usada (baixa do estoque)</Label>
                    <Input
                      type="number"
                      step="any"
                      min="0"
                      value={formData.fungicida_kg || ''}
                      onChange={(e) => setFormData({...formData, fungicida_kg: e.target.value})}
                      placeholder="Ex: 1 (opcional)"
                      className="text-sm"
                      data-testid="record-fungicida-qtd"
                    />
                  </div>
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.ethrel}
                  onChange={(e) => setFormData({...formData, ethrel: e.target.checked})}
                  className="mr-2"
                />
                ⚡ Ethrel
                <HelpIcon tooltip="Ethrel é um estimulante de látex que aumenta a produção. Registre aqui quando fizer aplicação para acompanhar o intervalo entre aplicações e receber alertas automáticos." />
              </Label>
              {formData.ethrel && (
                <div className="space-y-2 pl-2 border-l-2 border-orange-200 bg-orange-50 p-2 rounded">
                  <div>
                    <Label className="text-sm flex items-center">
                      📍 Talhão/Lote do Ethrel *
                      <HelpIcon tooltip="Informe em qual talhão ou lote foi feita a aplicação. Ex: Talhão A1, Lote 3, Quadra Norte" />
                    </Label>
                    <Input
                      type="text"
                      value={formData.talhao_lote_ethrel}
                      onChange={(e) => setFormData({...formData, talhao_lote_ethrel: e.target.value})}
                      placeholder="Ex: Talhão A1, Lote 3..."
                      className="text-sm"
                      required={formData.ethrel}
                    />
                  </div>
                  <div>
                    <Label className="text-sm flex items-center">
                      📊 Proporção/Concentração
                      <HelpIcon tooltip="Informe a concentração do Ethrel usada na aplicação. Pode ser em porcentagem (2.5%), proporção (1:10) ou ml por litro (25ml/L)." />
                    </Label>
                    <Input
                      type="text"
                      value={formData.ethrel_proporcao}
                      onChange={(e) => setFormData({...formData, ethrel_proporcao: e.target.value})}
                      placeholder="Ex: 2.5%, 1:10, 25ml/L..."
                      className="text-sm"
                    />
                  </div>
                  <div>
                    <Label className="text-sm">📦 Qtd usada em L (baixa do estoque)</Label>
                    <Input
                      type="number"
                      step="any"
                      min="0"
                      value={formData.ethrel_litros || ''}
                      onChange={(e) => setFormData({...formData, ethrel_litros: e.target.value})}
                      placeholder="Ex: 1 (opcional)"
                      className="text-sm"
                      data-testid="record-ethrel-qtd"
                    />
                  </div>
                  {/* Example tip */}
                  <div className="bg-blue-50 border border-blue-200 rounded p-2 mt-2">
                    <p className="text-xs text-blue-700">
                      <strong>💡 Exemplo:</strong> Talhão A1, Proporção 2.5% - O sistema irá alertá-lo quando estiver próximo da próxima aplicação baseado no intervalo configurado.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Estoque: protetor e herbicida (baixa automática) */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-sm">🌧️ Protetores colocados/repostos</Label>
              <Input
                type="number"
                step="any"
                min="0"
                value={formData.protetor_qtd || ''}
                onChange={(e) => setFormData({...formData, protetor_qtd: e.target.value})}
                placeholder="Qtd (baixa do estoque)"
                className="text-sm"
                data-testid="record-protetor-qtd"
              />
            </div>
            <div>
              <Label className="text-sm">🌿 Herbicida aplicado (L)</Label>
              <Input
                type="number"
                step="any"
                min="0"
                value={formData.herbicida_litros || ''}
                onChange={(e) => setFormData({...formData, herbicida_litros: e.target.value})}
                placeholder="Litros (baixa do estoque)"
                className="text-sm"
                data-testid="record-herbicida-qtd"
              />
            </div>
          </div>
          <p className="text-[10px] text-gray-400 -mt-2">📦 A baixa do estoque acontece ao salvar um registro novo — editar ou excluir o registro não mexe no estoque.</p>

          {/* Weather */}
          <div>
            <Label className="flex items-center">🌧️ Chuva (mm)<HelpIcon tooltip={t('tooltipChuva')} /></Label>
            <Input
              type="number"
              step="0.1"
              value={formData.chuva_mm}
              onChange={(e) => setFormData({...formData, chuva_mm: e.target.value})}
              placeholder="0.0"
              min="0"
            />
          </div>

          {/* Extra Activity */}
          <div>
            <Label>➕ Atividade Extra</Label>
            <Input
              type="text"
              value={formData.atividade_extra}
              onChange={(e) => setFormData({...formData, atividade_extra: e.target.value})}
              placeholder="Ex: Poda, manutenção, aplicação especial..."
            />
          </div>

          {/* Team Control Section (NEW - MINIMALISTA) */}
          <div className="border-t pt-4">
            <h4 className="font-medium text-gray-800 mb-3">👥 Controle da Equipe</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label className="text-sm">👤 Trabalhador Responsável</Label>
                <Input
                  type="text"
                  value={formData.trabalhador_responsavel}
                  onChange={(e) => setFormData({...formData, trabalhador_responsavel: e.target.value})}
                  placeholder="Ex: João Silva"
                  className="text-sm"
                />
                <p className="text-xs text-gray-500 mt-1">{t('rfOptionalProductivity')}</p>
              </div>
              <div>
                <Label className="text-sm">🌳 Plantas do Talhão</Label>
                <Input
                  type="number"
                  value={formData.plantas_talhao}
                  onChange={(e) => setFormData({...formData, plantas_talhao: e.target.value})}
                  placeholder="Ex: 3000"
                  min="0"
                  className="text-sm"
                />
                <p className="text-xs text-gray-500 mt-1">{t('rfOptionalKgPlant')}</p>
              </div>
            </div>

            {/* Compensação de Falta - aparece apenas quando há sangria + trabalhador + faltas pendentes */}
            {pendingAbsences.length > 0 && (formData.sangria || (formData.sangrias && formData.sangrias.length > 0)) && (
              <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid="compensacao-section">
                <div className="flex items-start gap-2 mb-2">
                  <input
                    type="checkbox"
                    id="compensa-falta-checkbox"
                    checked={!!formData.compensa_falta_id}
                    onChange={(e) => {
                      if (e.target.checked && pendingAbsences.length > 0) {
                        setFormData({...formData, compensa_falta_id: pendingAbsences[0].id});
                      } else {
                        setFormData({...formData, compensa_falta_id: ''});
                      }
                    }}
                    className="w-4 h-4 mt-0.5"
                    data-testid="compensa-falta-checkbox"
                  />
                  <Label htmlFor="compensa-falta-checkbox" className="text-sm font-medium text-amber-900 cursor-pointer">
                    🔁 {t('rfCompensateAbsenceTitle') || 'Esta sangria compensa uma falta'}
                  </Label>
                </div>
                {formData.compensa_falta_id && (
                  <div className="ml-6 mt-2">
                    <Label className="text-xs text-amber-900">{t('rfCompensateAbsenceSelect') || 'Qual falta está sendo compensada?'}</Label>
                    <select
                      value={formData.compensa_falta_id}
                      onChange={(e) => setFormData({...formData, compensa_falta_id: e.target.value})}
                      className="w-full mt-1 h-9 rounded-md border border-amber-300 bg-white px-3 text-sm"
                      data-testid="compensa-falta-select"
                    >
                      {pendingAbsences.map(f => (
                        <option key={f.id} value={f.id}>
                          {new Date(f.date + 'T00:00:00').toLocaleDateString('pt-BR')}
                          {f.motivo ? ` — ${f.motivo}` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                <p className="text-[11px] text-amber-700 mt-2 ml-6">
                  💡 {t('rfCompensateAbsenceHint') || 'Ao marcar, a falta selecionada será registrada como compensada nos relatórios.'}
                </p>
              </div>
            )}
          </div>

          {/* Internal Evaluation Section (Optional/Private) */}
          <div className="border-t pt-4">
            <div className="flex items-center space-x-2 mb-3">
              <input
                type="checkbox"
                checked={formData.use_internal_evaluation}
                onChange={(e) => setFormData({...formData, use_internal_evaluation: e.target.checked})}
                className="w-4 h-4"
                id="internal-eval-checkbox"
              />
              <Label htmlFor="internal-eval-checkbox" className="text-sm font-medium text-gray-700">
                🔒 Controle Interno (Privado)
              </Label>
            </div>
            <p className="text-xs text-gray-500 mb-3">
              ⚠️ Informações visíveis apenas para administradores desta propriedade
            </p>
            
            {/* NOVA SEÇÃO: Avaliação Simplificada do Trabalhador Responsável */}
            {formData.trabalhador_responsavel && !formData.use_internal_evaluation && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3">
                <div className="flex items-center space-x-2 mb-2">
                  <input
                    type="checkbox"
                    checked={formData.use_internal_evaluation}
                    onChange={(e) => setFormData({
                      ...formData, 
                      use_internal_evaluation: e.target.checked,
                      trabalhadores: e.target.checked ? formData.trabalhador_responsavel : '',
                      avaliacao_interna: e.target.checked ? '' : ''
                    })}
                    className="w-4 h-4"
                    id="quick-eval-checkbox"
                  />
                  <Label htmlFor="quick-eval-checkbox" className="text-sm font-medium text-blue-800">
                    ⭐ Avaliar {formData.trabalhador_responsavel} hoje?
                  </Label>
                </div>
                <p className="text-xs text-blue-600">
                  Clique para avaliar o desempenho deste trabalhador (opcional)
                </p>
              </div>
            )}
            
            {formData.use_internal_evaluation && (
              <div className="space-y-3 pl-4 border-l-2 border-gray-200 bg-gray-50 p-3 rounded">
                {/* Tooltip explicativo da Avaliação Interna */}
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
                  <div className="font-semibold mb-2">📋 Avaliação Interna — Seu controle de gestão:</div>
                  <ol className="space-y-1 ml-4 list-decimal">
                    <li>{t('rfEvalNote1')}</li>
                    <li>{t('rfEvalNote2')} <strong>{t('rfEvalNote2Bold')}</strong> {t('rfEvalNote2End')}</li>
                    <li>{t('rfEvalNote3')}</li>
                    <li><strong>{t('rfEvalNote4Bold')}</strong> {t('rfEvalNote4')}</li>
                  </ol>
                  <div className="mt-2 pt-2 border-t border-amber-300 flex items-center gap-1">
                    <span>🔒</span>
                    <span><strong>{t('rfPrivacyNote')}</strong> {t('rfPrivacyDesc')}</span>
                  </div>
                </div>
                
                {/* AVALIAÇÃO SIMPLIFICADA: Se há trabalhador responsável */}
                {formData.trabalhador_responsavel ? (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 bg-white rounded border">
                      <div>
                        <Label className="text-sm font-medium">👤 {formData.trabalhador_responsavel}</Label>
                        <p className="text-xs text-gray-500">{t('rfEvalScale')}</p>
                      </div>
                      <div className="flex items-center space-x-2">
                        <select
                          value={(() => {
                            const match = formData.avaliacao_interna.match(new RegExp(`${formData.trabalhador_responsavel}\\s*:\\s*(\\d+)`));
                            return match ? match[1] : '';
                          })()}
                          onChange={(e) => {
                            const score = e.target.value;
                            if (score) {
                              setFormData({
                                ...formData,
                                trabalhadores: formData.trabalhador_responsavel,
                                avaliacao_interna: `${formData.trabalhador_responsavel}: ${score}`
                              });
                            } else {
                              setFormData({
                                ...formData,
                                trabalhadores: '',
                                avaliacao_interna: ''
                              });
                            }
                          }}
                          className="px-3 py-1 border rounded text-sm"
                        >
                          <option value="">{t('rfSelectOption')}</option>
                          <option value="10">10 - Excelente</option>
                          <option value="9">9 - Muito Bom</option>
                          <option value="8">8 - Bom</option>
                          <option value="7">7 - Regular+</option>
                          <option value="6">6 - Regular</option>
                          <option value="5">5 - Precisa Melhorar</option>
                          <option value="4">4 - Abaixo Esperado</option>
                          <option value="3">3 - Ruim</option>
                          <option value="2">2 - Muito Ruim</option>
                          <option value="1">1 - Inaceitável</option>
                        </select>
                      </div>
                    </div>
                  </div>
                ) : (
                  /* MODO AVANÇADO: Múltiplos trabalhadores */
                  <div className="space-y-3">
                    <div>
                      <Label className="text-sm">👥 Trabalhadores do Dia</Label>
                      <Input
                        type="text"
                        value={formData.trabalhadores}
                        onChange={(e) => setFormData({...formData, trabalhadores: e.target.value})}
                        placeholder="Ex: João, Maria, Carlos..."
                        className="text-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-sm">📊 Avaliação Interna (1-10)</Label>
                      <Input
                        type="text"
                        value={formData.avaliacao_interna}
                        onChange={(e) => setFormData({...formData, avaliacao_interna: e.target.value})}
                        placeholder="Ex: João: 8, Maria: 7, Carlos: 9..."
                        className="text-sm"
                      />
                      <p className="text-xs text-gray-400 mt-1">
                        Para uso interno apenas - não será compartilhado publicamente
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Notes */}
          <div>
            <Label>📝 Observações</Label>
            <Textarea
              value={formData.observacoes}
              onChange={(e) => setFormData({...formData, observacoes: e.target.value})}
              placeholder="Observações adicionais sobre este dia..."
              rows={3}
            />
          </div>

          {/* Opção de Duplicar após salvar */}
          {showDuplicateOption && (
            <div className="bg-green-50 border-2 border-green-300 rounded-lg p-4 space-y-3">
              <div className="flex items-center gap-2 text-green-800">
                <span className="text-xl">✅</span>
                <span className="font-semibold">{t('rfRecordSaved')}</span>
              </div>
              <p className="text-sm text-green-700">
                Deseja registrar a produção de outro trabalhador para o mesmo dia?
              </p>
              <div className="flex gap-2">
                <Button 
                  type="button" 
                  onClick={handleDuplicate}
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                >
                  👥 Adicionar Outro Trabalhador
                </Button>
                <Button 
                  type="button" 
                  variant="outline"
                  onClick={handleCloseWithoutDuplicate}
                  className="flex-1"
                >
                  ✓ Concluir
                </Button>
              </div>
            </div>
          )}

          {/* Botões de ação */}
          {!showDuplicateOption && (
            <div className="flex gap-2 pt-4">
              <Button type="button" variant="outline" onClick={onClose} className="flex-1">
                Cancelar
              </Button>
              <Button type="submit" disabled={loading} className="flex-1 bg-green-600 hover:bg-green-700">
                {loading ? t('saving') : existingRecord ? t('update') : t('save')}
              </Button>
            </div>
          )}
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default RecordFormDialog;
